import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Checkbox,
  Chip,
  LinearProgress,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { taskService } from '../services/api';

function SubtaskList({ task, projectMembers, onSubtaskChange }) {
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localSubtasks, setLocalSubtasks] = useState(task?.subtasks || []);

  // Edition inline
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  // Suppression
  const [deleteDialog, setDeleteDialog] = useState({ open: false, subtask: null });

  useEffect(() => {
    setLocalSubtasks(task?.subtasks || []);
  }, [task?.subtasks]);

  const completed = localSubtasks.filter(s => s.status === 'completed').length;
  const total = localSubtasks.length;
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  const handleAddSubtask = async () => {
    if (!newTitle.trim()) return;
    setLoading(true);
    try {
      await taskService.createSubtask(task.id, { title: newTitle.trim() });
      setNewTitle('');
      setAdding(false);
      if (onSubtaskChange) onSubtaskChange();
    } catch (err) {
      console.error('Failed to create subtask:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (subtask) => {
    const newStatus = subtask.status === 'completed' ? 'not_started' : 'completed';
    setLocalSubtasks(prev => prev.map(s => s.id === subtask.id ? { ...s, status: newStatus } : s));
    try {
      await taskService.updateStatus(subtask.id, newStatus);
      if (onSubtaskChange) onSubtaskChange();
    } catch (err) {
      console.error('Failed to update subtask status:', err);
      setLocalSubtasks(task?.subtasks || []);
    }
  };

  const handleStartEdit = (subtask) => {
    setEditingId(subtask.id);
    setEditTitle(subtask.title);
  };

  const handleSaveEdit = async (subtask) => {
    if (!editTitle.trim()) return;
    setLocalSubtasks(prev => prev.map(s => s.id === subtask.id ? { ...s, title: editTitle.trim() } : s));
    setEditingId(null);
    try {
      await taskService.update(subtask.id, { title: editTitle.trim() });
      if (onSubtaskChange) onSubtaskChange();
    } catch (err) {
      console.error('Failed to update subtask:', err);
      setLocalSubtasks(task?.subtasks || []);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
  };

  const handleConfirmDelete = async () => {
    const subtask = deleteDialog.subtask;
    setDeleteDialog({ open: false, subtask: null });
    setLocalSubtasks(prev => prev.filter(s => s.id !== subtask.id));
    try {
      await taskService.delete(subtask.id);
      if (onSubtaskChange) onSubtaskChange();
    } catch (err) {
      console.error('Failed to delete subtask:', err);
      setLocalSubtasks(task?.subtasks || []);
    }
  };

  const getStatusColor = (status) => {
    const colors = { not_started: 'default', in_progress: 'info', completed: 'success', blocked: 'error' };
    return colors[status] || 'default';
  };

  if (!task) return null;

  return (
    <Box>
      {total > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {completed}/{total} sous-tâches terminées
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {progressPct}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{ height: 6, borderRadius: 3 }}
            color={progressPct === 100 ? 'success' : 'primary'}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {localSubtasks.map((subtask) => (
          <Box
            key={subtask.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
              '&:hover .subtask-actions': { opacity: 1 }
            }}
          >
            <Checkbox
              size="small"
              checked={subtask.status === 'completed'}
              onChange={() => handleToggleStatus(subtask)}
              sx={{ p: 0 }}
              disabled={editingId === subtask.id}
            />

            {editingId === subtask.id ? (
              <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit(subtask);
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  autoFocus
                  sx={{ flexGrow: 1 }}
                />
                <Button size="small" variant="contained" onClick={() => handleSaveEdit(subtask)} disabled={!editTitle.trim()}>
                  OK
                </Button>
                <Button size="small" onClick={handleCancelEdit}>
                  Annuler
                </Button>
              </Box>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    flexGrow: 1,
                    textDecoration: subtask.status === 'completed' ? 'line-through' : 'none',
                    color: subtask.status === 'completed' ? 'text.disabled' : 'text.primary'
                  }}
                >
                  {subtask.title}
                </Typography>
                {subtask.status !== 'not_started' && subtask.status !== 'completed' && (
                  <Chip label={subtask.status === 'in_progress' ? 'En cours' : 'Bloqué'} size="small" color={getStatusColor(subtask.status)} />
                )}
                {subtask.assignee_name && (
                  <Tooltip title={subtask.assignee_name}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.65rem',
                        fontWeight: 'bold',
                        flexShrink: 0
                      }}
                    >
                      {subtask.assignee_name.charAt(0)}
                    </Box>
                  </Tooltip>
                )}
                <Box className="subtask-actions" sx={{ display: 'flex', gap: 0, opacity: 0, transition: 'opacity 0.15s' }}>
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => handleStartEdit(subtask)} sx={{ p: 0.25 }}>
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton size="small" onClick={() => setDeleteDialog({ open: true, subtask })} sx={{ p: 0.25 }} color="error">
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}
          </Box>
        ))}
      </Box>

      <Collapse in={adding}>
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Titre de la sous-tâche..."
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddSubtask();
              if (e.key === 'Escape') { setAdding(false); setNewTitle(''); }
            }}
            autoFocus
            disabled={loading}
          />
          <Button size="small" variant="contained" onClick={handleAddSubtask} disabled={!newTitle.trim() || loading}>
            Ajouter
          </Button>
          <Button size="small" onClick={() => { setAdding(false); setNewTitle(''); }}>
            Annuler
          </Button>
        </Box>
      </Collapse>

      {!adding && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAdding(true)}
          sx={{ mt: 1 }}
          color="inherit"
        >
          Ajouter une sous-tâche
        </Button>
      )}

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, subtask: null })} maxWidth="xs">
        <DialogTitle>Supprimer la sous-tâche ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Êtes-vous sûr de vouloir supprimer "{deleteDialog.subtask?.title}" ? Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, subtask: null })}>Annuler</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Supprimer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SubtaskList;
